import { Box, Dialog, DialogContent, IconButton } from '@mui/material';
import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import BackArrow from '@mui/icons-material/ArrowBackIosNew';
import NextArrow from '@mui/icons-material/ArrowForwardIos';

interface ImageGalleryModalProps {
  images: any;
  imageDefaultIndex?: number;
  openModal: boolean;
  setOpenModal: (val: any) => void;
  children: React.ReactNode;
}

export const ImageGalleryModal = forwardRef(function ImageGalleryModal(
  props: ImageGalleryModalProps,
  ref: ForwardedRef<unknown>
) {
  const { images, imageDefaultIndex = 0, setOpenModal, openModal, children } = props;

  const [index, setIndex] = useState(imageDefaultIndex);
  const [imagesString, setImagesString] = useState('');

  useImperativeHandle(
    ref,
    () => ({
      handleOpenModal(index: number = 0) {
        setOpenModal(() => true);
        setIndex(index);
      },
    }),
    []
  );

  const handleNext = () => {
    console.log('nextIndex', (index + 1) % images.length);
    setIndex((index + 1) % images.length);
    setImagesString('');
  };

  const handlePrev = () => {
    setIndex((index - 1 + images.length) % images.length);
    setImagesString('');
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const getBase64Image = async (res: any) => {
    const blob = await res.blob();
    const reader = new FileReader();

    await new Promise((resolve, reject) => {
      reader.onload = resolve;
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    return reader.result;
  };

  const accessToken = localStorage.getItem('accessToken');
  const apiUrl = import.meta.env.VITE_SIMPLE_REST_URL || 'http://localhost:3000';

  useEffect(() => {
    if (images[index]) {
      fetch(`${apiUrl}/files/${images[index].id}/content`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
        .then(getBase64Image)
        .then(imgString => setImagesString(imgString));
    }
  }, [index]);

  if (!images) return null;
  if (!images.length) return null;
  if (!images[index]) return null;

  return (
    <>
      {children}
      <Dialog open={openModal} onClose={handleCloseModal} fullScreen={true}>
        <IconButton
          aria-label="close"
          onClick={handleCloseModal}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent
          sx={{
            backgroundColor: 'black',
            alignContent: 'center',
            textAlign: 'center',
          }}
        >
          <Box alignItems="center" display="flex">
            {images.length > 1 && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                bgcolor="#FFF"
                borderRadius={100}
                p={1}
                sx={{ '&:hover': { cursor: 'pointer' } }}
              >
                <BackArrow onClick={handlePrev} />
              </Box>
            )}

            <Box flex={1}>
              {imagesString.length === 0 ? (
                <Box
                  sx={{
                    display: 'inline-block',
                    marginRight: '10px',
                    width: '111px',
                    height: '111px',
                    backgroundImage: `url(data:${images[index].mimeType};base64,${images[index].thumbnail})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderRadius: '1em',
                    '&:hover': {
                      cursor: 'pointer',
                    },
                  }}
                />
              ) : (
                <img
                  src={imagesString}
                  style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 64px)' }}
                  alt="image"
                />
              )}
            </Box>

            {images.length > 1 && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                bgcolor="#FFF"
                borderRadius={100}
                p={1}
                sx={{ '&:hover': { cursor: 'pointer' } }}
              >
                <NextArrow onClick={handleNext} />
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
});
